/**
 * The command palette (Ctrl+K): one search field over everything the app can do and everything
 * on the board. It gathers, each time it opens:
 *
 * - every line of the menu bar's menus (`shell`), sub-menus included, with its shortcut;
 * - every window, to open it;
 * - the groups and nodes of the board worked on (the mirror's scoped graph), to jump to them.
 *
 * Nothing is listed by hand: a service adding a menu line adds it here too. Picks are
 * remembered in the browser and come first next time. The ranking is a pure function
 * ({@link rank}), tested without a page; the overlay is `app/CommandPalette.tsx`.
 */
import { createMemo, createSignal, type Accessor } from "solid-js";
import type { Keybind } from "@protocol/Keybind";
import type { GraphMirror } from "../client/graph";
import type { Commands, CommandSet } from "../shared/commands";
import { WINDOW_GROUPS, isItem, type MenuBarId, type MenuEntry, type MenuSection } from "../shared/menus";
import { load, save } from "../shared/storage";
import type { Shell } from "./shell";
import type { View } from "./view";

/** What a palette line stands for (also the order of equal matches). */
export type PaletteKind = "action" | "window" | "group" | "node";

/** One line of the palette. */
export interface PaletteItem {
  /** Stable key (`action:edit/History/Undo`, `window:log`, `node:<id>`), remembered for recent picks. */
  key: string;
  /** What the line says. */
  label: string;
  /** Where it comes from ("Edit › History", "Window", "Node · class"), also searched. */
  where: string;
  /** What it stands for. */
  kind: PaletteKind;
  /** Its keyboard shortcut, as the config writes it (`Ctrl+Z`), or `""`. */
  shortcut: string;
  /** A switch's state: on, off, or not a switch. */
  checked?: boolean;
  /** Shown but cannot be run now. */
  disabled?: boolean;
  /** Deletes or replaces something. */
  danger?: boolean;
  /** Asks to be chosen twice; the first time shows this text. */
  confirm?: string;
  /** Does it. */
  run: () => void;
}

/** How many lines the palette shows at most. */
export const PALETTE_LIMIT = 60;

/** How many recent picks are remembered. */
const RECENT_COUNT = 12;

/** Where recent picks are kept in the browser. */
const RECENT_KEY = "developer-app-palette-recent";

/** Order of kinds among equal matches. */
const KIND_ORDER: Record<PaletteKind, number> = { action: 0, window: 1, group: 2, node: 3 };

/**
 * The clickable lines of a menu as palette items, sub-menus flattened (their parent's label
 * goes in `where`). `run(command, argument)` runs a named command. Lines under a heading in
 * `skip` are left out.
 */
export function menuItems(
  menu: { id: MenuBarId; label: string },
  entries: readonly MenuEntry[],
  run: (command: string, argument: string | null) => void,
  shortcut: (command: string | undefined, argument: string | null) => string,
  skip: readonly string[] = [],
): PaletteItem[] {
  const out: PaletteItem[] = [];
  /** Walks `list` under the path `path`. */
  const walk = (list: readonly MenuEntry[], path: string[]) => {
    let heading = "";
    for (const entry of list) {
      if ("heading" in entry) heading = entry.heading;
      if (!isItem(entry)) continue;
      if (path.length === 1 && skip.includes(heading)) continue;
      const where = [...path, ...(heading && path.length === 1 ? [heading] : [])];
      if (entry.items) {
        walk(entry.items, [...where, entry.label]);
        continue;
      }
      const { command, action } = entry;
      const argument = entry.argument ?? null;
      if (!command && !action) continue;
      out.push({
        key: `action:${menu.id}/${where.slice(1).join("/")}/${entry.label}`,
        label: entry.label,
        where: where.join(" › "),
        kind: "action",
        shortcut: shortcut(command, argument),
        checked: entry.checked,
        disabled: entry.disabled,
        danger: entry.danger,
        confirm: entry.confirm,
        run: () => (command ? run(command, argument) : action?.()),
      });
    }
  };
  walk(entries, [menu.label]);
  return out;
}

/**
 * How well one word of a search matches `label` (lower case), or `null` when it does not:
 * the whole label, its start, the start of one of its words, inside it, then its letters in
 * order (closer together is better).
 */
export function wordScore(label: string, word: string): number | null {
  if (label === word) return 100;
  if (label.startsWith(word)) return 80;
  if (new RegExp(`[^a-z0-9]${escape(word)}`).test(label)) return 60;
  if (label.includes(word)) return 40;
  let at = -1;
  let first = -1;
  for (const ch of word) {
    at = label.indexOf(ch, at + 1);
    if (at < 0) return null;
    if (first < 0) first = at;
  }
  // Letters in order: better when they sit close together.
  return Math.max(1, 20 - (at - first - word.length + 1));
}

/** `text` with the characters a regular expression gives a meaning escaped. */
function escape(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * How well `query` matches an item, or `null` when one of its words matches neither the
 * label nor where the item comes from (which counts less).
 */
export function itemScore(item: Pick<PaletteItem, "label" | "where">, query: string): number | null {
  const label = item.label.toLowerCase();
  const where = item.where.toLowerCase();
  let total = 0;
  for (const word of query.toLowerCase().split(/\s+/).filter(Boolean)) {
    const inLabel = wordScore(label, word);
    const score = inLabel ?? (where.includes(word) ? 10 : null);
    if (score === null) return null;
    total += score;
  }
  return total;
}

/**
 * The lines to show for `query`, best first, at most `limit`. With no query: the recent picks,
 * then every action and window (nodes and groups only come when searched for). Recent picks
 * count for more, the most recent most; equal matches keep actions, windows, groups, nodes in
 * that order, then the order they came in.
 */
export function rank(items: readonly PaletteItem[], query: string, recent: readonly string[], limit = PALETTE_LIMIT): PaletteItem[] {
  const recency = (key: string) => {
    const i = recent.indexOf(key);
    return i < 0 ? 0 : 30 - i;
  };
  const scored: { item: PaletteItem; score: number; i: number }[] = [];
  items.forEach((item, i) => {
    if (!query.trim()) {
      if (item.kind !== "action" && item.kind !== "window" && !recent.includes(item.key)) return;
      scored.push({ item, score: recency(item.key) * 10, i });
      return;
    }
    const score = itemScore(item, query);
    if (score !== null) scored.push({ item, score: score + recency(item.key), i });
  });
  scored.sort((a, b) => b.score - a.score || KIND_ORDER[a.item.kind] - KIND_ORDER[b.item.kind] || a.i - b.i);
  return scored.slice(0, limit).map((s) => s.item);
}

/** `recent` with `key` first, once, at most {@link RECENT_COUNT} long. */
export function remembered(recent: readonly string[], key: string): string[] {
  return [key, ...recent.filter((k) => k !== key)].slice(0, RECENT_COUNT);
}

/** What the palette is built from. */
export interface PaletteNeeds {
  /** The menu bar's menus. */
  shell: Pick<Shell, "menus" | "menu">;
  /** Runs the menu lines' commands. */
  commands: Pick<Commands, "run">;
  /** The windows, opened from the palette. */
  /** The windows (the window manager's). */
  wm: { windows(): readonly { id: string; title: string; group?: string }[]; open(id: string): void };
  /** The board worked on. */
  graph: Pick<GraphMirror, "state">;
  /** Selects and centres what was jumped to. */
  view: Pick<View, "selectNode" | "selectGroup" | "requestCenter">;
  /** The shortcuts now. */
  binds: () => Keybind[];
  /** A node kind's label ("Class"). */
  kindLabel?: (kind: string) => string;
}

/** The palette service. */
export interface Palette {
  /** Whether it is shown. */
  shown: Accessor<boolean>;
  /** Shows it, empty. */
  show(): void;
  /** Hides it. */
  hide(): void;
  /** Shows it if hidden, else hides it. */
  toggle(): void;
  /** What is typed. */
  query: Accessor<string>;
  /** Types it. */
  setQuery(query: string): void;
  /** The lines shown now, best first. */
  results: Accessor<PaletteItem[]>;
  /** The highlighted line's index in {@link results}. */
  active: Accessor<number>;
  /** Highlights the line `by` lines down (negative: up), round the ends. */
  move(by: number): void;
  /** Highlights the line at `index`. */
  setActive(index: number): void;
  /** The key of the line chosen once that asks to be chosen twice. */
  armed: Accessor<string | null>;
  /** Runs a line (the highlighted one when none is given) and hides the palette; does nothing for a greyed one. */
  choose(item?: PaletteItem): void;
}

/** Creates the palette. */
export function createPalette(needs: PaletteNeeds): Palette {
  const [shown, setShown] = createSignal(false);
  const [query, setQueryNow] = createSignal("");
  const [active, setActive] = createSignal(0);
  const [armed, setArmed] = createSignal<string | null>(null);
  const saved = load<unknown>(RECENT_KEY, []);
  const [recent, setRecent] = createSignal<string[]>(Array.isArray(saved) ? saved.filter((k): k is string => typeof k === "string") : []);
  /** Bumped each time the palette opens, so the menus are asked again (they follow the state). */
  const [opened, setOpened] = createSignal(0);

  /** The keys bound to a command, as written in the config. */
  const shortcut = (command: string | undefined, argument: string | null) =>
    command ? (needs.binds().find((b) => b.action === command && (b.argument ?? null) === argument)?.keys ?? "") : "";

  /** Everything the palette can show, gathered now. */
  const items = createMemo(() => {
    opened();
    if (!shown()) return [];
    const run = (command: string, argument: string | null) => void needs.commands.run(command, argument);
    const actions = needs.shell.menus().flatMap((menu) => menuItems(menu, needs.shell.menu(menu.id), run, shortcut, menu.id === "window" ? [...WINDOW_GROUPS, "Other"] : []));
    // A command reachable from two menus (Help's shortcut list repeats them) is listed once.
    const seen = new Set<string>();
    const unique = actions.filter((item) => {
      const id = `${item.label}\n${item.shortcut}`;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    const windows: PaletteItem[] = needs.wm.windows().map((w) => ({
      key: `window:${w.id}`,
      label: w.title,
      where: `Window › ${w.group ?? "Other"}`,
      kind: "window",
      shortcut: shortcut("window.toggle", w.id) || shortcut("window.open", w.id),
      run: () => needs.wm.open(w.id),
    }));
    const graph = needs.graph.state.graph;
    const groups: PaletteItem[] = graph.groups.map((g) => ({
      key: `group:${g.id}`,
      label: g.name,
      where: g.collapsed ? "Group · folded" : "Group",
      kind: "group",
      shortcut: "",
      run: () => {
        needs.view.selectGroup(g.id);
        const member = graph.nodes.find((n) => n.group === g.id);
        if (member) needs.view.requestCenter(member.id);
      },
    }));
    const nodes: PaletteItem[] = graph.nodes.map((n) => ({
      key: `node:${n.id}`,
      label: n.name,
      where: `${needs.kindLabel?.(n.kind) ?? n.kind}${n.page ? " · page" : ""}`,
      kind: "node",
      shortcut: "",
      run: () => {
        needs.view.selectNode(n.id);
        needs.view.requestCenter(n.id);
      },
    }));
    return [...unique, ...windows, ...groups, ...nodes];
  });
  const results = createMemo(() => rank(items(), query(), recent()));

  const hide = () => {
    setShown(false);
    setArmed(null);
  };
  const show = () => {
    setQueryNow("");
    setActive(0);
    setArmed(null);
    setOpened((n) => n + 1);
    setShown(true);
  };
  return {
    shown,
    show,
    hide,
    toggle: () => (shown() ? hide() : show()),
    query,
    setQuery(text) {
      setQueryNow(text);
      setActive(0);
      setArmed(null);
    },
    results,
    active,
    move(by) {
      const count = results().length;
      if (count) setActive((i) => (((i + by) % count) + count) % count);
      setArmed(null);
    },
    setActive,
    armed,
    choose(item = results()[active()]) {
      if (!item || item.disabled) return;
      if (item.confirm && armed() !== item.key) {
        setArmed(item.key);
        return;
      }
      const next = remembered(recent(), item.key);
      setRecent(next);
      save(RECENT_KEY, next);
      hide();
      item.run();
    },
  };
}

/** The palette's command: `palette.open` shows or hides it. */
export function paletteCommands(palette: Palette): CommandSet {
  return { "palette.open": () => palette.toggle() };
}

/** Help → Find an action…, at the top of the Help menu. */
export function paletteMenus(): MenuSection[] {
  return [{ menu: "help", section: "Find", order: 5, entries: () => [{ label: "Find an action…", command: "palette.open" }] }];
}
